import { useMemo, useState } from 'react';
import {
  Card,
  CardBody,
  Input,
  Badge,
  Alert,
  Button,
  ButtonGroup,
  FormGroup,
  Label,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import { useTaxRates, useDeleteTaxRate } from './useTaxRates';
import { TaxRateModal } from './TaxRateModal';

/**
 * Tax-Rates CRUD-View.
 *
 * Spec: lmm-wiki/wiki/orbit-specs/done/admin-tax-matrix-editable.md
 * Endpoints: GET / POST / PUT / DELETE /v1/admin/tax-rates
 *
 * Defaults (aus utils/vatRates.js) sind read-only-anzeigbar; können per
 * Edit zu Overrides hochgestuft werden. Overrides sind editierbar und
 * via Reset auf Default zurücksetzbar (entspricht DELETE).
 *
 * Compliance:
 *  - Order-Snapshot bleibt versionssicher (existierende Orders unverändert)
 *  - Audit-Log Pflicht via reason-Feld → action='tax_rate.*'
 *  - 60s Cache-TTL nach DELETE für Resolver-Update
 */
export function TaxRatesPage() {
  const query = useTaxRates();
  const deleteMutation = useDeleteTaxRate();
  const canWrite = useHasPermission({ perms: ['taxRates.write'] });

  const [search, setSearch] = useState('');
  const [ossOnly, setOssOnly] = useState(false);
  const [overridesOnly, setOverridesOnly] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const rates = useMemo(() => {
    let list = query.data?.rates ?? [];
    if (ossOnly) list = list.filter((r) => r.ossEligible);
    if (overridesOnly) list = list.filter((r) => r.source === 'override');
    const term = search.trim().toUpperCase();
    if (term) {
      list = list.filter((r) =>
        r.country.includes(term) ||
        countryName(r.country).toUpperCase().includes(term));
    }
    return [...list].sort((a, b) => a.country.localeCompare(b.country));
  }, [query.data, search, ossOnly, overridesOnly]);

  const overrideCount = (query.data?.rates ?? []).filter((r) => r.source === 'override').length;

  const onDeleteConfirm = async () => {
    if (!pendingDelete) return;
    await deleteMutation.mutateAsync({ country: pendingDelete.country });
    setPendingDelete(null);
  };

  return (
    <div>
      <PageHeader
        title="Tax-Rates"
        subtitle={`EU-Defaults + DB-Overrides · ${overrideCount} Override(s) aktiv`}
        actions={
          <HasPermission perms={['taxRates.write']}>
            <Button color="primary" onClick={() => setCreating(true)}>
              + Neuer Override
            </Button>
          </HasPermission>
        }
      />

      <Alert color="info" className="small">
        Defaults kommen aus <code>{query.data?.sourceFile ?? 'utils/vatRates.js'}</code>.
        Overrides werden in der DB persistiert und überschreiben die Defaults
        (60s Cache-TTL). Bestehende Orders bleiben durch den{' '}
        <code>tax_rate_snapshot_pct</code>-Snapshot unverändert.{' '}
        <Link to="/valikey/audit?action=tax_rate">
          Audit-Trail anzeigen →
        </Link>{' '}
        {query.data?.serverStartedAt && (
          <span className="text-muted">
            · Server gestartet <RelativeTime iso={query.data.serverStartedAt} />
          </span>
        )}
      </Alert>

      <Card className="mb-3">
        <CardBody className="py-2">
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <FormGroup className="mb-0 flex-grow-1" style={{ maxWidth: 320 }}>
              <Label className="small text-muted mb-1">Suche</Label>
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="ISO oder Ländername…"
                bsSize="sm"
              />
            </FormGroup>
            <FormGroup check className="mb-0">
              <Input
                type="checkbox"
                id="oss-only"
                checked={ossOnly}
                onChange={(e) => setOssOnly(e.target.checked)}
              />
              <Label check for="oss-only" className="small">
                Nur OSS-eligible
              </Label>
            </FormGroup>
            <FormGroup check className="mb-0">
              <Input
                type="checkbox"
                id="overrides-only"
                checked={overridesOnly}
                onChange={(e) => setOverridesOnly(e.target.checked)}
              />
              <Label check for="overrides-only" className="small">
                Nur Overrides
              </Label>
            </FormGroup>
            <div className="ms-auto small text-muted">
              {rates.length} von {query.data?.rates?.length ?? 0}
            </div>
          </div>
        </CardBody>
      </Card>

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && rates.length === 0}
        emptyMessage={search || ossOnly || overridesOnly ? 'Keine Treffer für die aktuellen Filter.' : 'Keine Tax-Rates.'}
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 70 }}>ISO</th>
            <th>Land</th>
            <th className="text-end" style={{ width: 90 }}>Standard</th>
            <th className="text-end" style={{ width: 90 }}>Reduced</th>
            <th className="text-center" style={{ width: 70 }}>OSS</th>
            <th style={{ width: 180 }}>Source</th>
            <th style={{ width: 150 }} className="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rates.map((r) => (
            <tr key={`${r.country}-${r.categorySlug ?? ''}`}>
              <td><code className="fw-semibold">{r.country}</code></td>
              <td>{countryName(r.country)}</td>
              <td className="text-end">{formatRate(r.standard)}</td>
              <td className="text-end">
                {r.reduced != null
                  ? formatRate(r.reduced)
                  : <span className="text-muted">—</span>}
              </td>
              <td className="text-center">
                {r.ossEligible
                  ? <Badge color="success" pill>✓</Badge>
                  : <Badge color="secondary" pill>—</Badge>}
              </td>
              <td>
                {r.source === 'override' ? (
                  <div>
                    <Badge color="warning" pill className="me-1">override</Badge>
                    {r.overriddenAt && (
                      <span className="small text-muted">
                        <RelativeTime iso={r.overriddenAt} />
                      </span>
                    )}
                    {r.reason && (
                      <div className="small fst-italic text-truncate" style={{ maxWidth: 220 }} title={r.reason}>
                        {r.reason}
                      </div>
                    )}
                  </div>
                ) : (
                  <Badge color="light" className="text-muted">default</Badge>
                )}
              </td>
              <td className="text-end">
                {canWrite && (
                  <ButtonGroup size="sm">
                    <Button
                      color="primary"
                      outline
                      onClick={() => setEditingRow(r)}
                      disabled={deleteMutation.isPending}
                    >
                      ✏️ Edit
                    </Button>
                    {r.source === 'override' && (
                      <Button
                        color="danger"
                        outline
                        onClick={() => setPendingDelete(r)}
                        disabled={deleteMutation.isPending}
                        title="Override entfernen, Fallback auf Default"
                      >
                        ↺ Reset
                      </Button>
                    )}
                  </ButtonGroup>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      {editingRow && (
        <TaxRateModal row={editingRow} mode="edit" onClose={() => setEditingRow(null)} />
      )}
      {creating && (
        <TaxRateModal row={null} mode="create" onClose={() => setCreating(false)} />
      )}

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title={pendingDelete ? `Override für ${pendingDelete.country} entfernen?` : ''}
        body={
          pendingDelete && (
            <>
              <p className="mb-2">
                Override für <strong>{countryName(pendingDelete.country)}</strong>{' '}
                (<code>{pendingDelete.country}</code>) wird gelöscht.
              </p>
              <p className="mb-0 small text-muted">
                {hasDefault(pendingDelete.country)
                  ? `Fallback auf EU-Default aktiv binnen 60s (Resolver-Cache-TTL). Bestehende Orders bleiben durch ihren tax_rate_snapshot_pct unverändert.`
                  : 'Kein EU-Default für dieses Land — nach Reset ist die Country-Rate undefined und Calls würden 0% Export liefern.'}
              </p>
            </>
          )
        }
        confirmLabel="Reset"
        confirmColor="danger"
        busy={deleteMutation.isPending}
        onConfirm={onDeleteConfirm}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}

function formatRate(n) {
  if (n == null) return '—';
  return `${Number.isInteger(n) ? n : n.toFixed(1)} %`;
}

const EU_COUNTRIES = new Set([
  'AT','BE','BG','CY','CZ','DE','DK','EE','ES','FI','FR','GR','EL','HR','HU',
  'IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK',
]);
function hasDefault(country) {
  return EU_COUNTRIES.has(country);
}

const COUNTRY_NAMES = {
  AT: 'Österreich', BE: 'Belgien', BG: 'Bulgarien', CY: 'Zypern', CZ: 'Tschechien',
  DE: 'Deutschland', DK: 'Dänemark', EE: 'Estland', ES: 'Spanien', FI: 'Finnland',
  FR: 'Frankreich', EL: 'Griechenland', GR: 'Griechenland', HR: 'Kroatien',
  HU: 'Ungarn', IE: 'Irland', IT: 'Italien', LT: 'Litauen', LU: 'Luxemburg',
  LV: 'Lettland', MT: 'Malta', NL: 'Niederlande', PL: 'Polen', PT: 'Portugal',
  RO: 'Rumänien', SE: 'Schweden', SI: 'Slowenien', SK: 'Slowakei',
  CH: 'Schweiz', NO: 'Norwegen', UK: 'Vereinigtes Königreich', GB: 'Vereinigtes Königreich',
  US: 'USA', CA: 'Kanada',
};
function countryName(iso) {
  return COUNTRY_NAMES[iso] ?? iso;
}
